import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getCsrfToken } from '../utils/apiClient';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook for tracking "new items" across modules.
 * Fetches unread counts from the backend and provides mark-seen functionality.
 */
export function useActivityTracker() {
  const [counts, setCounts] = useState({ bank_feeds: 0, invoices: 0, bills: 0 });
  const [lastSeen, setLastSeen] = useState({});
  const intervalRef = useRef(null);

  const fetchCounts = useCallback(async () => {
    try {
      if (!getCsrfToken()) return;
      const res = await axios.get(`${API}/api/activity/unread-counts`);
      setCounts(res.data);
    } catch {
      // Silently fail
    }
  }, []);

  const fetchLastSeen = useCallback(async () => {
    try {
      if (!getCsrfToken()) return;
      const res = await axios.get(`${API}/api/activity/last-seen`);
      setLastSeen(res.data.last_seen || {});
    } catch {
      // Silently fail
    }
  }, []);

  const markSeen = useCallback(async (module) => {
    try {
      if (!getCsrfToken()) return;
      await axios.post(`${API}/api/activity/mark-seen?module=${module}`);
      // Update local state immediately
      setCounts(prev => ({ ...prev, [module]: 0 }));
      setLastSeen(prev => ({ ...prev, [module]: new Date().toISOString() }));
    } catch {
      // Silently fail
    }
  }, []);

  // Clear badge count in UI only (no server update). Used by sidebar to clear badge on page entry
  // while letting the page itself retain lastSeen for row-level "NEW" badges.
  const clearCount = useCallback((module) => {
    setCounts(prev => ({ ...prev, [module]: 0 }));
  }, []);

  useEffect(() => {
    fetchCounts();
    fetchLastSeen();
    // Refresh every 60 seconds
    intervalRef.current = setInterval(fetchCounts, 60000);
    return () => clearInterval(intervalRef.current);
  }, [fetchCounts, fetchLastSeen]);

  return { counts, lastSeen, markSeen, clearCount, refreshCounts: fetchCounts };
}
